import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Cta,
  Footer,
  Icon,
  ItemRow,
  Keypad,
  Strip,
  V1Screen,
  WaitCta,
} from '@/features/gate/v1/kit'
import { C, typeKey } from '@/features/gate/v1/style'
import { kg, shortCode, shortLot, tr } from '@/features/gate/v1/text'
import type { CloseRunPayload, RecordOutputPayload } from '@/features/process/commands'
import '@/features/process/commands'
import {
  isRunLive,
  liveOutputs,
  runParentIds,
  type ProcessState,
  type RunView,
} from '@/features/process/projection'
import { runFigures } from '@/features/process/model/rules'
import { useDraftState } from '@/features/process/useProcess'
import { freshCodeV1 } from '@/features/process/v1/model'
import { PScanner, refusalText, ScalePad, useScaleEntry } from '@/features/process/v1/parts'
import {
  Grid2,
  NextArrow,
  PickTile,
  SackChip,
  SaveError,
  saveFailed,
  SavedLine,
  TagChip,
  TickDot,
} from '@/features/process/v1/ui'
import { DoneScreen, IconRows, Lead, Locked, Note, PackHeader, WithHall, YesSheet } from './ui'

type View =
  | { kind: 'home' }
  | { kind: 'tag' }
  | { kind: 'weigh'; code: string; entry: 'SCAN' | 'TYPED' }
  | { kind: 'count' }

type CountKey = 'emptied' | 'destroyed' | 'film'

interface Counts {
  emptied: number | null
  destroyed: number | null
  film: number | null
}

const HOME = '/m/pack'

const emptyCounts = (): Counts => ({ emptied: null, destroyed: null, film: null })

/**
 * V1 — रुके पाउच खाली करें (F7-B1.c): empty held pouches into new ordinary sacks (red tie, new
 * tag, weighed), count the pouches emptied and destroyed whole and the film destroyed, then close
 * the run. Same rules and commands as the full DelabelPage (record_output per sack, close_run with
 * the three counts); our label is never reused, the film is counted and destroyed.
 */
export function V1Delabel() {
  const { runId = '' } = useParams()
  const navigate = useNavigate()
  return (
    <WithHall>
      {(state) => {
        const run = state.runs.get(runId)
        if (!run || run.step_code !== 'DELABEL')
          return (
            <V1Screen
              header={
                <PackHeader
                  onBack={() => navigate(HOME)}
                  icon="pouch"
                  title={tr('pack.v1.delabel.missing')}
                />
              }
            >
              <Strip icon="bang">{tr('pack.v1.delabel.missingLine')}</Strip>
            </V1Screen>
          )
        return <Delabel key={run.id} state={state} run={run} />
      }}
    </WithHall>
  )
}

function Delabel({ state, run }: { state: ProcessState; run: RunView }) {
  const navigate = useNavigate()
  const actor = useActor()
  const step = state.steps.get('DELABEL')
  const outKey = step?.outputs[0]?.key ?? 'EMPTIED'
  const pouches = Number(run.params.pouches ?? 0)
  const lotCode = String(run.params.pack_lot_code ?? '')
  const tail = lotCode ? shortLot(lotCode) : '—'
  const live = liveOutputs(run)
  const liveNet = live.reduce((a, o) => a + o.net_g, 0)
  const defaultSack =
    state.pack.sack_types.find((s) => s.default_output) ?? state.pack.sack_types[0]
  const [sackId, setSackId] = useState<string | null>(defaultSack?.id ?? null)
  const sack = state.pack.sack_types.find((s) => s.id === sackId)
  const scale = useScaleEntry(state, sack?.tare_g ?? 0)
  const [view, setView] = useState<View>({ kind: 'home' })
  // The three counts are a draft for the run, so a closed app keeps what was typed.
  const [counts, setCounts, loaded, clearCounts] = useDraftState<Counts>(
    `pack.v1.delabel:${run.id}`,
    'pack.v1.delabel',
    emptyCounts,
    actor.user_id,
  )
  const [savedLine, setSavedLine] = useState<string | null>(null)
  const [sheet, setSheet] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<Counts | null>(null)
  const home = () => navigate(HOME)
  const header = { name: tr('pack.v1.lotTail', { tail }), line: tr('pack.v1.delabel.title') }

  if (!loaded && !done) return <V1Screen />

  if (done)
    return (
      <DoneScreen
        title={tr('pack.v1.delabel.done.title')}
        lines={[
          tr('pack.v1.delabel.done.line', { tail, count: pouches }),
          tr('pack.v1.delabel.job2Done', {
            emptied: done.emptied ?? 0,
            destroyed: done.destroyed ?? 0,
            film: done.film ?? 0,
          }),
        ]}
        todo={[tr('pack.v1.delabel.done.s1')]}
        cta={<Cta label={tr('pack.v1.home')} icon="home" lead onClick={home} />}
      />
    )

  if (!isRunLive(run.status) || run.closed_local)
    return (
      <DoneScreen
        title={tr('pack.v1.delabel.closed')}
        lines={[tr('pack.v1.delabel.done.line', { tail, count: pouches })]}
        todo={[]}
        cta={<Cta label={tr('pack.v1.home')} icon="home" lead onClick={home} />}
      />
    )

  // The full screen's rules: every pouch counted, a film for each emptied pouch, and a sack
  // weighed when any pouch was emptied.
  const { emptied, destroyed, film } = counts
  const typed = emptied !== null && destroyed !== null && film !== null
  const counted = (emptied ?? 0) + (destroyed ?? 0)
  const countsOk = typed && counted === pouches && film >= (emptied ?? 0)
  const ready = countsOk && ((emptied ?? 0) === 0 || live.length > 0)
  const usedHere = new Set(live.map((o) => o.qr_payload.toUpperCase()))

  const saveSack = async (code: string, entry: 'SCAN' | 'TYPED') => {
    if (!sack) return
    const reading = scale.submit()
    if (!reading) return
    setSaving(true)
    setErr(null)
    try {
      const container_id = newId()
      const payload: RecordOutputPayload = {
        container_id,
        unit_id: live[0]?.unit_id ?? newId(),
        run_id: run.id,
        qr_payload: code,
        entry,
        stream_key: outKey,
        grade: null,
        sack_type_id: sack.id,
        tare_g: sack.tare_g,
        weight_read_g: reading.read_g,
        confirmed_twice: reading.confirmed_twice,
        moisture_pct: null,
        tie: 'CONV',
      }
      await enqueueCommand({
        id: container_id,
        type: 'record_output',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      setSavedLine(
        tr('pack.v1.delabel.saved', { n: live.length + 1, kg: kg(reading.read_g - sack.tare_g) }),
      )
      scale.reset()
      setView({ kind: 'tag' })
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  const close = async () => {
    if (!ready) return
    setSaving(true)
    setErr(null)
    try {
      const fig = runFigures(run, step)
      const payload: CloseRunPayload = {
        run_id: run.id,
        ended_at: new Date().toISOString(),
        zero_streams: live.length ? [] : [outKey],
        params: { pouches_emptied: emptied!, pouches_destroyed: destroyed!, film_destroyed: film! },
        phone_totals: {
          input_g: fig.input_g,
          output_g: liveNet,
          bags_in: 0,
          sacks_out: live.length,
        },
      }
      await enqueueCommand({
        type: 'close_run',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      setSheet(false)
      setDone(counts)
      void clearCounts()
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  // नई बोरी पर टैग
  if (view.kind === 'tag') {
    const onCode = (code: string, entry: 'SCAN' | 'TYPED') => {
      const v = freshCodeV1(state, code, 'OUTPUT_BAG', actor.user_id, usedHere)
      if (!v.ok) return refusalText(v.reason)
      setErr(null)
      setView({ kind: 'weigh', code: v.value, entry })
      return null
    }
    return (
      <V1Screen
        header={
          <PackHeader
            onBack={() => (setSavedLine(null), setView({ kind: 'home' }))}
            lot={header}
            icon="tag"
            title={tr('pack.v1.delabel.tag.title')}
          />
        }
        footer={
          <Footer>
            {live.length > 0 ? (
              <Cta
                label={tr('pack.v1.delabel.tag.done')}
                icon="ok"
                onClick={() => (setSavedLine(null), setView({ kind: 'home' }))}
              />
            ) : (
              <WaitCta>{tr('pack.v1.delabel.tag.wait')}</WaitCta>
            )}
          </Footer>
        }
      >
        {savedLine && <SavedLine>{savedLine}</SavedLine>}
        <PScanner
          state={state}
          kind="OUTPUT_BAG"
          icon="redtie"
          hint={tr('pack.v1.delabel.tag.hint')}
          onCode={onCode}
        />
      </V1Screen>
    )
  }

  // बोरी तौलें
  if (view.kind === 'weigh') {
    const back = () => (scale.reset(), setView({ kind: 'tag' }))
    return (
      <V1Screen
        main={{ padding: '6px 12px', gap: 6 }}
        header={
          <PackHeader
            onBack={back}
            lot={header}
            icon="scale"
            title={tr('pack.v1.delabel.weigh.title')}
          />
        }
        footer={
          <Footer dense>
            <SaveError text={err} />
            {scale.value && sack ? (
              <Cta
                label={tr('pack.v1.delabel.weigh.save')}
                icon="ok"
                busy={saving}
                onClick={() => void saveSack(view.code, view.entry)}
              />
            ) : (
              <WaitCta>{tr('pack.v1.delabel.weigh.wait')}</WaitCta>
            )}
          </Footer>
        }
      >
        {state.pack.sack_types.length > 1 ? (
          <>
            <TagChip code={view.code} onBad={back} />
            <span style={{ fontSize: 15, fontWeight: 700, color: C.muted }}>
              {tr('pack.v1.delabel.weigh.sackType')}
            </span>
            <Grid2 gap={6}>
              {state.pack.sack_types.map((s) => (
                <PickTile
                  key={s.id}
                  pressed={s.id === sackId}
                  minHeight={48}
                  onClick={() => setSackId(s.id)}
                >
                  {s.name} · {tr('process.v1.kg', { kg: kg(s.tare_g) })}
                </PickTile>
              ))}
            </Grid2>
          </>
        ) : (
          <Grid2 gap={6}>
            <TagChip code={view.code} onBad={back} />
            {sack && <SackChip name={sack.name} tare={kg(sack.tare_g)} />}
          </Grid2>
        )}
        <ScalePad label={tr('pack.v1.delabel.weigh.label', { n: live.length + 1 })} entry={scale} />
      </V1Screen>
    )
  }

  // पाउच गिनें
  if (view.kind === 'count')
    return (
      <CountBoard
        header={header}
        pouches={pouches}
        counts={counts}
        setCounts={setCounts}
        onBack={() => setView({ kind: 'home' })}
      />
    )

  // The run's home: what to empty, the new sacks, the counts, then close.
  const needSacks = (emptied ?? 1) > 0 && live.length === 0
  // What the footer waits for, and the job it opens: the new sacks first (unless every pouch was
  // destroyed whole), then the counts.
  const target: 'tag' | 'count' = needSacks && (!typed || countsOk) ? 'tag' : 'count'
  const wait =
    target === 'tag'
      ? tr('pack.v1.delabel.wait.sacks')
      : !typed
        ? tr('pack.v1.delabel.wait.count')
        : counted !== pouches
          ? tr('pack.v1.delabel.count.gap', { n: counted, m: pouches })
          : tr('pack.v1.delabel.count.filmShort')
  const sacksDone = live.length > 0
  return (
    <V1Screen
      header={
        <PackHeader
          onBack={home}
          lot={header}
          icon="pouch"
          title={tr('pack.v1.delabel.title')}
          iconBg={C.bad}
        />
      }
      footer={
        <Footer>
          <SaveError text={err} />
          {ready ? (
            <Cta
              label={tr('pack.v1.delabel.close.cta')}
              icon="ok"
              onClick={() => (setErr(null), setSheet(true))}
            />
          ) : (
            <WaitCta onClick={() => (setErr(null), setView({ kind: target }))}>{wait}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        sheet ? (
          <YesSheet
            title={tr('pack.v1.delabel.close.title')}
            yes={tr('pack.v1.delabel.close.yes')}
            yesIcon="ok"
            busy={saving}
            onYes={() => void close()}
            onClose={() => setSheet(false)}
            top={180}
          >
            <IconRows
              rows={[
                { icon: 'pouch', label: tr('pack.v1.delabel.toEmpty'), value: String(pouches) },
                { label: tr('pack.v1.delabel.count.emptied'), value: String(emptied ?? 0) },
                { label: tr('pack.v1.delabel.count.destroyed'), value: String(destroyed ?? 0) },
                { label: tr('pack.v1.delabel.count.film'), value: String(film ?? 0) },
                {
                  icon: 'sack',
                  label: tr('pack.v1.delabel.job1'),
                  value: tr('pack.v1.delabel.close.sacks', {
                    count: live.length,
                    kg: kg(liveNet),
                  }),
                },
              ]}
            />
            <Locked>{tr('pack.v1.delabel.close.locked')}</Locked>
            <SaveError text={err} />
          </YesSheet>
        ) : undefined
      }
    >
      <section
        style={{
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
          borderRadius: 12,
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <span style={{ fontSize: 16, color: C.muted }}>{tr('pack.v1.delabel.toEmpty')}</span>
          <span style={{ fontSize: 15, color: C.muted }}>{lotCode || '—'}</span>
        </span>
        <b style={{ fontSize: 34, lineHeight: 1 }}>{pouches}</b>
      </section>
      <Note icon="redtie" tone="warn">
        {tr('pack.v1.delabel.rule')}
      </Note>
      <JobRow
        n={1}
        icon="sack"
        title={tr('pack.v1.delabel.job1')}
        line={
          sacksDone
            ? tr('pack.v1.delabel.job1Done', { count: live.length, kg: kg(liveNet) })
            : tr('pack.v1.delabel.job1Line')
        }
        done={sacksDone}
        current={!ready && target === 'tag'}
        onClick={() => (setErr(null), setView({ kind: 'tag' }))}
      />
      {live.map((o) => (
        <ItemRow
          key={o.container_id}
          padding="4px 10px"
          lead={<Icon name="redtie" size={24} style={{ color: C.bad }} />}
          title={
            <span style={{ fontSize: 16 }}>
              {tr('process.v1.tag', { no: shortCode(o.qr_payload) })}
            </span>
          }
          right={<b style={{ fontSize: 17 }}>{tr('process.v1.kg', { kg: kg(o.net_g) })}</b>}
        />
      ))}
      <JobRow
        n={2}
        icon="pencil"
        title={tr('pack.v1.delabel.job2')}
        line={
          typed
            ? tr('pack.v1.delabel.job2Done', {
                emptied: emptied!,
                destroyed: destroyed!,
                film: film!,
              })
            : tr('pack.v1.delabel.job2Line')
        }
        done={countsOk}
        current={!ready && target === 'count'}
        onClick={() => setView({ kind: 'count' })}
      />
    </V1Screen>
  )
}

/** One job of the run home (नई बोरी, गिनती), as on the pack line's आज. */
function JobRow({
  n,
  icon,
  title,
  line,
  done,
  current,
  onClick,
}: {
  n: number
  icon: 'sack' | 'pencil'
  title: string
  line: string
  done: boolean
  current: boolean
  onClick: () => void
}) {
  return (
    <ItemRow
      padding="10px 12px"
      onClick={onClick}
      border={current ? C.acc : C.border}
      borderWidth={current ? 2 : 1.5}
      lead={<Lead icon={icon} bg={current ? C.acc : C.sunk} color={current ? '#ffffff' : C.acc} />}
      title={<span style={{ fontSize: 20 }}>{title}</span>}
      line={<span style={{ color: C.muted }}>{line}</span>}
      right={
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {done ? (
            <TickDot size={30} />
          ) : (
            <span
              aria-hidden="true"
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                border: `2px solid ${current ? C.acc : C.border}`,
                color: current ? C.acc : C.border,
                fontSize: 18,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
            >
              {n}
            </span>
          )}
          <NextArrow color={current ? C.acc : C.muted} />
        </span>
      }
    />
  )
}

const COUNT_ROWS: Array<{ key: CountKey; label: () => string; hint?: () => string }> = [
  { key: 'emptied', label: () => tr('pack.v1.delabel.count.emptied') },
  { key: 'destroyed', label: () => tr('pack.v1.delabel.count.destroyed') },
  {
    key: 'film',
    label: () => tr('pack.v1.delabel.count.film'),
    hint: () => tr('pack.v1.delabel.count.filmHint'),
  },
]

/** पाउच गिनें: three counts on one keypad (tap a row to type into it). */
function CountBoard({
  header,
  pouches,
  counts,
  setCounts,
  onBack,
}: {
  header: { name: string; line: string }
  pouches: number
  counts: Counts
  setCounts: (fn: (c: Counts) => Counts) => void
  onBack: () => void
}) {
  const [active, setActive] = useState<CountKey>(
    () => COUNT_ROWS.find((r) => counts[r.key] === null)?.key ?? 'emptied',
  )
  const onKey = (k: string) =>
    setCounts((c) => {
      const cur = c[active] === null ? '' : String(c[active])
      const next = typeKey(cur, k, { decimals: 0, max: 6 })
      return { ...c, [active]: next === '' ? null : Number(next) }
    })
  const { emptied, destroyed, film } = counts
  const typed = emptied !== null && destroyed !== null && film !== null
  const counted = (emptied ?? 0) + (destroyed ?? 0)
  const gap = emptied !== null && destroyed !== null && counted !== pouches
  const filmShort = typed && film < emptied
  return (
    <V1Screen
      main={{ padding: '8px 12px', gap: 6 }}
      header={
        <PackHeader
          onBack={onBack}
          lot={header}
          icon="pencil"
          title={tr('pack.v1.delabel.count.title')}
          sub={`${tr('pack.v1.delabel.toEmpty')}: ${pouches}`}
        />
      }
      footer={
        <Footer dense>
          {typed && !gap && !filmShort ? (
            <Cta label={tr('pack.v1.delabel.count.done')} icon="ok" onClick={onBack} />
          ) : (
            <WaitCta>
              {gap
                ? tr('pack.v1.delabel.count.gap', { n: counted, m: pouches })
                : filmShort
                  ? tr('pack.v1.delabel.count.filmShort')
                  : tr('pack.v1.delabel.count.wait')}
            </WaitCta>
          )}
        </Footer>
      }
    >
      {COUNT_ROWS.map((r) => {
        const on = active === r.key
        const v = counts[r.key]
        return (
          <ItemRow
            key={r.key}
            padding="6px 10px"
            onClick={() => setActive(r.key)}
            border={on ? C.acc : C.rule}
            borderWidth={on ? 2 : 1.5}
            title={<span style={{ fontSize: 18 }}>{r.label()}</span>}
            line={
              r.hint ? <span style={{ fontSize: 14, color: C.muted }}>{r.hint()}</span> : undefined
            }
            right={
              <span
                style={{
                  minWidth: 72,
                  height: 44,
                  padding: '0 8px',
                  boxSizing: 'border-box',
                  borderRadius: 10,
                  border: on ? `2px solid ${C.acc}` : `1.5px solid ${C.border}`,
                  background: C.surface,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                }}
              >
                {v !== null ? (
                  <b style={{ fontSize: 24 }}>{v}</b>
                ) : (
                  <span style={{ fontSize: 20, color: C.muted }}>—</span>
                )}
                {on && (
                  <span aria-hidden="true" style={{ width: 3, height: 26, background: C.acc }} />
                )}
              </span>
            }
          />
        )
      })}
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Screen>
  )
}

/**
 * The live emptying runs of the hall (set at the console), one row each, as the full pack home
 * lists them first; on the pack line's आज.
 */
export function DelabelRows({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  const runs = [...state.runs.values()].filter(
    (r) => r.step_code === 'DELABEL' && isRunLive(r.status) && !r.closed_local,
  )
  return (
    <>
      {runs.map((r) => {
        const code = String(r.params.pack_lot_code ?? r.code)
        return (
          <ItemRow
            key={r.id}
            padding="8px 10px"
            onClick={() => navigate(`/m/pack/delabel/${r.id}`)}
            border={C.bad}
            borderWidth={2}
            lead={<Lead icon="pouch" bg={C.badWash} color={C.bad} />}
            title={tr('pack.v1.delabel.row', { tail: shortLot(code) })}
            line={
              <span style={{ fontSize: 15, color: C.muted }}>
                {tr('pack.v1.delabel.rowLine', { count: Number(r.params.pouches ?? 0) })}
              </span>
            }
            right={<NextArrow />}
          />
        )
      })}
    </>
  )
}
